import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { createSessionRefreshCoordinator } from "./sessionRefresh.mjs";

const safeStorageGet = key => {
	try {
		return localStorage.getItem(key);
	} catch (_err) {
		return null;
	}
};

const safeStorageSet = (key, value) => {
	try {
		localStorage.setItem(key, value);
	} catch (_err) {
		// Ignore storage restrictions on Safari private mode.
	}
};

const safeStorageRemove = key => {
	try {
		localStorage.removeItem(key);
	} catch (_err) {
		// Ignore storage restrictions on Safari private mode.
	}
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const lastAccessScheduleWarningRef = useRef(null);
	const forceLogoutRef = useRef(false);

	const applyUserLanguage = useCallback(nextUser => {
		const language = nextUser?.language || "pt-BR";
		i18n.changeLanguage(language);
		safeStorageSet("i18nextLng", language);
	}, []);

	const expireSession = useCallback(() => {
		safeStorageRemove("token");
		api.defaults.headers.Authorization = undefined;
		setIsAuth(false);
		setUser({});

		const currentPath = String(window.location.pathname || "");
		if (!["/login", "/signup"].includes(currentPath)) {
			history.replace("/login");
		}
	}, [history]);

	const forceAccessScheduleLogout = useCallback(message => {
		if (forceLogoutRef.current) return;
		forceLogoutRef.current = true;
		toast.warn(message || i18n.t("userModal.accessSchedule.forceLogoutToast"));
		expireSession();
	}, [expireSession]);

	useEffect(() => {
		const refreshSession = createSessionRefreshCoordinator({
			refresh: async () => {
				const { data } = await api.post("/auth/refresh_token");
				return data;
			},
			onSuccess: data => {
				if (!data?.token) return;
				safeStorageSet("token", JSON.stringify(data.token));
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
			},
			onFailure: expireSession
		});

		const requestInterceptor = api.interceptors.request.use(
			config => {
				const storedToken = safeStorageGet("token");
				if (storedToken) {
					config.headers = config.headers || {};
					config.headers.Authorization = `Bearer ${JSON.parse(storedToken)}`;
				}
				return config;
			},
			error => Promise.reject(error)
		);

		const responseInterceptor = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config;
				const requestUrl = originalRequest?.url || "";
				const hasStoredToken = Boolean(safeStorageGet("token"));
				const isPublicAuthRoute =
					requestUrl.includes("/auth/login") ||
					requestUrl.includes("/auth/signup") ||
					requestUrl.includes("/auth/refresh_token");

				if (
					error?.response?.status === 403 &&
					originalRequest &&
					!originalRequest._retry &&
					hasStoredToken &&
					!isPublicAuthRoute
				) {
					originalRequest._retry = true;

					try {
						const data = await refreshSession();
						originalRequest.headers = originalRequest.headers || {};
						originalRequest.headers.Authorization = `Bearer ${data.token}`;
						return api(originalRequest);
					} catch (refreshError) {
						return Promise.reject(refreshError);
					}
				}

				if (error?.response?.status === 401 && !isPublicAuthRoute) {
					expireSession();
				}
				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, [expireSession]);

	useEffect(() => {
		const token = safeStorageGet("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
					applyUserLanguage(data.user);
				} catch (err) {
					expireSession();
				}
			}
			setLoading(false);
		})();
	}, [applyUserLanguage, expireSession]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
				applyUserLanguage(data.user);
			}
		});

		socket.on("accessScheduleForceLogout", () => {
			forceAccessScheduleLogout(i18n.t("userModal.accessSchedule.forceLogoutToast"));
		});

		return () => {
			socket.disconnect();
		};
	}, [applyUserLanguage, forceAccessScheduleLogout, user]);

	useEffect(() => {
		if (!isAuth || !user?.id || user?.userType === "ai_agent") return undefined;

		const checkAccessSchedule = async () => {
			try {
				const { data } = await api.get("/user-access-schedules/status");
				if (!data?.enforced) return;

				if (!data.allowed) {
					forceAccessScheduleLogout(i18n.t("userModal.accessSchedule.forceLogoutToast"));
					return;
				}

				if (data.warning && Number.isFinite(Number(data.minutesUntilEnd))) {
					const minutes = Math.max(0, Math.ceil(Number(data.minutesUntilEnd)));
					const warningKey = `${user.id}:${minutes}`;
					if (lastAccessScheduleWarningRef.current !== warningKey) {
						lastAccessScheduleWarningRef.current = warningKey;
						toast.warn(
							i18n.t("userModal.accessSchedule.warningToast", { minutes }),
							{ autoClose: 10000 }
						);
					}
				}
			} catch (err) {
				if (err?.response?.data?.error === "ERR_ACCESS_SCHEDULE_OUTSIDE_HOURS") {
					forceAccessScheduleLogout(i18n.t("userModal.accessSchedule.forceLogoutToast"));
				}
			}
		};

		checkAccessSchedule();
		const interval = setInterval(checkAccessSchedule, 60000);

		return () => clearInterval(interval);
	}, [forceAccessScheduleLogout, isAuth, user]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			safeStorageSet("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			applyUserLanguage(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
		} catch (_err) {
			// Logout must always complete locally even if server token is invalid.
		} finally {
			setIsAuth(false);
			setUser({});
			safeStorageRemove("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
